import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useApi, errMsg } from "../../lib/api";
import { useMe } from "../../components/AppShell";
import { PageHeader } from "../../components/common";

function Subgroups({ group, labels, reload }) {
  const api = useApi();
  const [name, setName] = useState("");
  const add = async () => { await api.post(`/groups/${group.id}/subgroups`, { name }); setName(""); reload(); };
  const del = async (id) => { await api.delete(`/groups/${group.id}/subgroups/${id}`); reload(); };
  return (
    <div className="panel p-5" data-testid="subgroups-panel"><div className="overline mb-3">{labels.sub}</div>
      <div className="flex flex-wrap gap-2 mb-4">{group.subgroups.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1 rounded-full border border-slate-600 pl-3 pr-1 py-1 text-sm" data-testid={`subgroup-${s.id}`}>{s.name}
          <button onClick={() => del(s.id)} data-testid={`delete-subgroup-${s.id}`} className="p-1 hover:text-red-400"><X size={12} /></button></span>))}
        {group.subgroups.length === 0 && <span className="text-sm text-slate-500">None yet.</span>}</div>
      <div className="flex gap-2"><Input data-testid="subgroup-name-input" placeholder={`New ${labels.subOne.toLowerCase()} name`} value={name} onChange={(e) => setName(e.target.value)} />
        <Button data-testid="add-subgroup-btn" disabled={!name.trim()} onClick={add}>Add</Button></div>
    </div>
  );
}

function Staff({ group, kind, title, hint, reload }) {
  const api = useApi();
  const [email, setEmail] = useState("");
  const list = group[kind === "nurse" ? "nurse_emails" : "admin_emails"] || [];
  const add = async () => { try { await api.post(`/groups/${group.id}/staff`, { email, kind }); setEmail(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (e) => { await api.delete(`/groups/${group.id}/staff/${kind}/${encodeURIComponent(e)}`); reload(); };
  return (
    <div className="panel p-5" data-testid={`staff-${kind}`}><div className="overline mb-1">{title}</div><div className="text-xs text-slate-400 mb-3">{hint}</div>
      {list.map((e) => <div key={e} className="flex justify-between items-center text-sm py-1"><span>{e}</span><button data-testid={`remove-${kind}-${e}`} onClick={() => del(e)}><X size={14} /></button></div>)}
      <div className="flex gap-2 mt-2"><Input data-testid={`${kind}-email-input`} type="email" placeholder="email@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button data-testid={`add-${kind}-btn`} disabled={!email.includes("@")} onClick={add}>Add</Button></div>
    </div>
  );
}

export default function Settings() {
  const { group, labels, reload } = useOutletContext();
  const { reload: reloadNav } = useMe();
  const api = useApi();
  const [name, setName] = useState(group.name);
  const save = async (patch) => {
    try { await api.patch(`/groups/${group.id}/settings`, patch); toast.success("Saved"); reload(); if (patch.name) reloadNav(); } catch (e) { toast.error(errMsg(e)); }
  };
  const digest = async () => { const r = await api.post(`/groups/${group.id}/digest`); toast.success(`Digest sent to ${r.data.sent} contacts (${r.data.people} people)`); };
  return (
    <div data-testid="settings-page" className="max-w-4xl">
      <PageHeader overline="Group" title="Settings" />
      <div className="grid md:grid-cols-2 gap-4">
        <div className="panel p-5"><div className="overline mb-3">Name</div>
          <div className="flex gap-2"><Input data-testid="group-rename-input" value={name} onChange={(e) => setName(e.target.value)} /><Button data-testid="group-rename-btn" onClick={() => save({ name })}>Save</Button></div></div>
        <div className="panel p-5" data-testid="alert-rules-panel"><div className="overline mb-3">Alert rules</div>
          <label className="flex items-start justify-between gap-4 text-sm mb-4"><span>Send emergency alerts to group admins too<span className="block text-xs text-slate-400">Parents / guardians always receive them.</span></span>
            <Switch data-testid="alert-admins-switch" checked={!!group.settings.alert_admins} onCheckedChange={(v) => save({ alert_admins: v })} /></label>
          <div className="text-sm mb-2">Check-in / check-out messages to guardians</div>
          <Select value={group.settings.checkin_alerts} onValueChange={(v) => save({ checkin_alerts: v })}>
            <SelectTrigger data-testid="checkin-alerts-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">None (default)</SelectItem><SelectItem value="digest">Daily digest</SelectItem><SelectItem value="instant">Instant message per event</SelectItem></SelectContent></Select>
          {group.settings.checkin_alerts === "digest" && <Button variant="outline" size="sm" className="mt-3" data-testid="send-digest-btn" onClick={digest}>Send today's digest now</Button>}
          <label className="flex items-start justify-between gap-4 text-sm mt-5"><span>Show medical info at reader stations<span className="block text-xs text-slate-400">Only fields each person shows to responders. Off = name and photo only.</span></span>
            <Switch data-testid="reader-medical-switch" checked={group.settings.reader_medical !== false} onCheckedChange={(v) => save({ reader_medical: v })} /></label>
        </div>
        <Subgroups group={group} labels={labels} reload={reload} />
        <Staff group={group} kind="admin" title="Co-admins" hint="Can manage members, tags and orders. Never see medical data." reload={reload} />
        {group.type !== "family" && <Staff group={group} kind="nurse" title="Nurses (read-only)" hint="See full profiles only when the guardian grants access. Every view is audit-logged." reload={reload} />}
      </div>
    </div>
  );
}
